import { Component, OnChanges, Input, SimpleChanges, Injector, OnInit } from '@angular/core';
import { ApAlertService } from 'webcommon/core-ui';
import { FormControl, AbstractControl, FormGroup } from '@angular/forms';
import { FcItemListBaseComponent, FcListValidationService } from '../../generic/index';
import { ApColorSelectorService } from './../../../shared/index';
import { FcItemModalContext, FcItemModalMode } from '../../../models/index';
import { FcPatientProgramListItemDto } from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-patient-program-general-tab',
  templateUrl: './fc-patient-program-general-tab.component.html',
  styleUrls: ['./fc-patient-program-general-tab.component.less']
})
export class FcPatientProgramGeneralTabComponent extends FcItemListBaseComponent implements OnChanges, OnInit {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: FcPatientProgramListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;
  @Input() isDataLoaded: boolean;

  public isNewMode: boolean;

  // Form Controls
  public externalIdControl = new FormControl<string>('');
  public nameControl = new FormControl<string>('');
  public patientProgramUidControl = new FormControl<string>(this.systemGuid.GuidEmpty);
  public toolbarTextControl = new FormControl<string>('');
  public textColorControl = new FormControl<string>('');
  public colorControl = new FormControl<string>('');
  public notesControl = new FormControl<string>('');
  public inactiveControl = new FormControl<boolean>(false);
  public userDefinedControl = new FormControl<boolean>(true);

  constructor(
    injector: Injector,
    public apColorSelectorService: ApColorSelectorService,
    public alertService: ApAlertService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.setForm();
    this.setTextColorValue();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.isNewMode = this.itemContext.mode === FcItemModalMode.New;
      this.fcListValidation.initValidation(this.itemContext, {
        ExternalID: {
          MaxLength: 25,
        },
        Name: {
          IsRequired: true,
          MaxLength: 100,
        },
        ToolbarText: {
          MaxLength: 10,
        },
        Notes: {
          IsRequired: false,
          MaxLength: 65535,
        },
      });
    }

    if (changes.form != null) {
      this.addControlsToForm();
    }
    if ((changes.itemContext != null || changes.form != null || changes.listItem != null) && this.isDataLoaded) {
      this.setForm();
    }
  }

  public addControlsToForm(): void {
    this.form.addControl('externalId', this.externalIdControl);
    this.form.addControl('name', this.nameControl);
    this.form.addControl('patientProgramUid', this.patientProgramUidControl);
    this.form.addControl('toolbarText', this.toolbarTextControl);
    this.form.addControl('textColor', this.textColorControl);
    this.form.addControl('color', this.colorControl);
    this.form.addControl('notes', this.notesControl);
    this.form.addControl('inactive', this.inactiveControl);
    this.form.addControl('userDefined', this.userDefinedControl);
  }

  public override showErrors(control: AbstractControl, isSubmitted: boolean = false): boolean {
    return FcListValidationService.showErrors(control, isSubmitted);
  }

  public setTextColorValue(): void {
    this.apColorSelectorService.appliedColor$
      .pipe(this.takeUntilDestroy())
      .subscribe((color: string) => {
        this.colorControl.setValue(color);
      });
  }

  public setForm(): void {
    // PatientProgramUid Control
    this.fcListValidation.setValidatorsAndValue(this.patientProgramUidControl, 'PatientProgramUid', this.listItem.PatientProgramUid);

    // External Id Control
    this.setTextColorValue();
    this.fcListValidation.setValidatorsAndValue(this.externalIdControl, 'ExternalID', this.listItem.ExternalID);

    // Text Color Control
    this.fcListValidation.setValidatorsAndValue(this.colorControl, 'Color', this.listItem.Color);
    this.apColorSelectorService.setAppliedColor(this.listItem.Color);

    // Name Control
    const isNameControlDisabled = !this.listItem.UserDefined;
    if (isNameControlDisabled) {
      this.nameControl.disable();
    }
    this.fcListValidation.setValidatorsAndValue(this.nameControl, 'Name', this.listItem.Name);

    // Toolbar Text Control
    this.fcListValidation.setValidatorsAndValue(this.toolbarTextControl, 'ToolbarText', this.listItem.ToolbarText);

    // Notes Control
    this.fcListValidation.setValidatorsAndValue(this.notesControl, 'Notes', this.listItem.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive ?? false);

    // User Defined Control
    this.userDefinedControl.disable();
    this.userDefinedControl.setValue(this.listItem.UserDefined ?? true);
  }
}
